import { useEffect, useState } from "react";
import { supabase } from "@vinoga/db";
import { ChevronLeft, ChevronRight as ChevronRightIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n";

const PAGE_SIZE = 50;
// "Abandoned" = converted_order_id IS NULL AND last_activity_at < now() - <window>.
// Window options surfaced as a filter chip; default = 24h.
const WINDOW_OPTIONS = [
  { value: 1, labelKey: "carts.window1h", labelFallback: "Idle > 1h" },
  { value: 24, labelKey: "carts.window24h", labelFallback: "Idle > 24h" },
  { value: 24 * 7, labelKey: "carts.window7d", labelFallback: "Idle > 7d" },
  { value: 24 * 30, labelKey: "carts.window30d", labelFallback: "Idle > 30d" },
] as const;

export function AbandonedCarts() {
  const { toast } = useToast();
  const t = useT();

  const [carts, setCarts] = useState<any[]>([]);
  const [itemCounts, setItemCounts] = useState<Record<string, number>>({});
  const [itemTotals, setItemTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [windowHours, setWindowHours] = useState<number>(24);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const cutoff = new Date(Date.now() - windowHours * 60 * 60 * 1000).toISOString();
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, count, error } = await supabase
        .from("carts")
        .select("*", { count: "exact" })
        .is("converted_order_id", null)
        .lt("last_activity_at", cutoff)
        .order("last_activity_at", { ascending: false })
        .range(from, to);

      if (cancelled) return;

      if (error) {
        toast({
          title: t("carts.errorLoading", "Error loading abandoned carts"),
          description: error.message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const cartIds = (data ?? []).map((c: any) => c.id);
      let cartsWithItems = data ?? [];

      if (cartIds.length) {
        const { data: items } = await supabase
          .from("cart_items")
          .select("cart_id, qty, unit_price")
          .in("cart_id", cartIds);
        const cmap: Record<string, number> = {};
        const tmap: Record<string, number> = {};
        (items ?? []).forEach((i: any) => {
          cmap[i.cart_id] = (cmap[i.cart_id] ?? 0) + Number(i.qty);
          tmap[i.cart_id] =
            (tmap[i.cart_id] ?? 0) + Number(i.qty) * Number(i.unit_price);
        });
        // Filter out carts with no items (abandonment requires items)
        cartsWithItems = cartsWithItems.filter((c: any) => (cmap[c.id] ?? 0) > 0);
        setItemCounts(cmap);
        setItemTotals(tmap);
      } else {
        setItemCounts({});
        setItemTotals({});
      }

      setCarts(cartsWithItems);
      setTotalCount(count ?? 0);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [windowHours, page, t, toast]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const formatIdleSince = (date: string) => {
    const d = new Date(date);
    const diffMs = Date.now() - d.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-xl font-bold text-foreground">
          {t("carts.title", "Abandoned Carts")}
          {totalCount > 0 && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({totalCount.toLocaleString()})
            </span>
          )}
        </h1>
        <Select
          value={String(windowHours)}
          onValueChange={(v) => {
            setWindowHours(Number(v));
            setPage(0);
          }}
        >
          <SelectTrigger className="h-8 w-[140px] font-body text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={String(opt.value)}>
                {t(opt.labelKey, opt.labelFallback)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <p className="text-xs text-muted-foreground">
        {t(
          "carts.subtitle",
          "Carts that were never converted to a paid order. Recovery email automation lands in a follow-up."
        )}
      </p>

      {loading ? (
        <div className="p-4 font-body text-sm text-muted-foreground">
          {t("common.loading", "Loading...")}
        </div>
      ) : carts.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground">
          {t("carts.empty", "No abandoned carts in this window.")}
        </p>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-bold">
                    {t("carts.colCustomer", "Customer")}
                  </TableHead>
                  <TableHead className="font-bold">{t("carts.colEmail", "Email")}</TableHead>
                  <TableHead className="text-center font-bold">
                    {t("carts.colItems", "Items")}
                  </TableHead>
                  <TableHead className="text-right font-bold">
                    {t("carts.colSubtotal", "Subtotal")}
                  </TableHead>
                  <TableHead className="font-bold">
                    {t("carts.colIdleFor", "Idle for")}
                  </TableHead>
                  <TableHead className="text-right font-bold">
                    {t("carts.colLastActivity", "Last activity")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {carts.map((c) => (
                  <TableRow key={c.id} className="hover:bg-muted/30">
                    <TableCell className="py-2.5">
                      {[c.customer_first_name, c.customer_last_name].filter(Boolean).join(" ") || (
                        <span className="text-muted-foreground italic">
                          {t("carts.anonymous", "Anonymous")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-2.5 text-muted-foreground">
                      {c.customer_email ?? (
                        <span className="italic">{t("carts.noEmail", "No email")}</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2.5 text-center font-medium">
                      {itemCounts[c.id] ?? 0}
                    </TableCell>
                    <TableCell className="py-2.5 text-right">
                      €{(itemTotals[c.id] ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="py-2.5">
                      <Badge variant="outline">{formatIdleSince(c.last_activity_at)}</Badge>
                    </TableCell>
                    <TableCell className="py-2.5 text-right text-muted-foreground">
                      <span className="font-body text-xs">
                        {new Date(c.last_activity_at).toLocaleString(undefined, {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-1">
              <span className="font-body text-xs text-muted-foreground">
                {t("common.showing", "Showing")} {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, totalCount)} {t("common.of", "of")}{" "}
                {totalCount.toLocaleString()}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={14} />
                </Button>
                <span className="px-3 font-body text-xs text-foreground">
                  {t("common.page", "Page")} {page + 1} {t("common.of", "of")} {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRightIcon size={14} />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
}
